/**
 * LLM Query Metadata (backend). No P1 prompt text is stored in the codebase.
 * P1 prompts come from Secret Manager (production) or P1_PROMPTS_DIR (development).
 */
import { DEFAULT_MODEL } from './llmConstants';
import ddxIngestPrompts from '@healthdesk/ddx-ingest-prompts';

const ddxIngestPromptsMod = ddxIngestPrompts?.default ?? ddxIngestPrompts;
const DDX_AI_REVIEW_SYSTEM_PROMPT =
  typeof ddxIngestPromptsMod?.DDX_AI_REVIEW_SYSTEM_PROMPT === 'string'
    ? ddxIngestPromptsMod.DDX_AI_REVIEW_SYSTEM_PROMPT
    : '';
const DDX_GROUNDED_FIX_SYSTEM_PROMPT =
  typeof ddxIngestPromptsMod?.DDX_GROUNDED_FIX_SYSTEM_PROMPT === 'string'
    ? ddxIngestPromptsMod.DDX_GROUNDED_FIX_SYSTEM_PROMPT
    : '';
const DDX_GROUNDED_FIX_RESPONSE_FORMAT =
  typeof ddxIngestPromptsMod?.DDX_GROUNDED_FIX_RESPONSE_FORMAT === 'string'
    ? ddxIngestPromptsMod.DDX_GROUNDED_FIX_RESPONSE_FORMAT
    : '';

const SC4_SYMPTOM_DETAILS_AUGMENT_SYSTEM_PROMPT = `You generate additional symptom-detail questions for Symptom Checker 4.

Mission:
- Return up to 3 extra questions that improve discrimination among the provided differential diagnoses.
- Questions must be useful for support/elimination of diagnoses (not generic intake drift).

Hard constraints:
- Symptom-details only (timing, progression, severity, quality, location/radiation, aggravating/relieving factors, functional impact).
- Never ask demographics, consent, proxy, broad ROS fishing, medication allergies, social history, or unrelated screening.
- Avoid duplicates with provided transcript/current asked questions.
- Keep each question concise, patient-facing, and single-question.
- Output valid JSON only per response format.`;

/** Shared P1 prompt for ChartMind V2 chart generation (ED/PC/UC); aligns with legacy ChartMind v1 documentation rules. */
const CHARTMIND_V2_CHART_GENERATION_P1_PROMPT = `You are a medical documentation assistant. Generate a structured clinical note from the patient encounter transcript.

CRITICAL REQUIREMENTS:
1. ABSOLUTE PROHIBITION: You must NEVER include any fictional, fabricated, or assumed information. Only document what is explicitly stated or clearly demonstrated in the transcript.
2. For any section or subsection not present in the transcript, leave it EMPTY after the colon (e.g., "HEENT:" with nothing after) - do NOT write "Not documented" or any placeholder text, and do NOT fabricate normal findings.
3. Do NOT add symptoms, findings, diagnoses, or any clinical information that was not explicitly mentioned.
4. Physical Exam follows the SAME empty-data rule: for each PE system not documented in the transcript, leave it empty after the colon. Do NOT fill in presumptive normal findings.`;

const RESPONSE_FORMATS = {
  'chartmind-ddx': `Return ONLY valid JSON - no markdown, no code fences, no extra text.
All boolean values must be true or false (not "yes", "no", "Yes", "No").
Every diagnosis MUST include: condition, likelihood, urgent, rationale, reasoning, supporting_factors.
Do not truncate or omit any required fields.

EXPECTED JSON FORMAT:
{
  "primary_diagnoses": [
    {
      "condition": "Condition Name",
      "likelihood": "More Likely",
      "urgent": false,
      "rationale": "Brief clinical reasoning",
      "reasoning": "Detailed clinical reasoning chain: symptom A → suggests mechanism B → combined with finding C → supports diagnosis D",
      "supporting_factors": ["Symptom A", "Finding C", "Risk factor from history"]
    }
  ],
  "alternative_diagnoses": [
    {
      "condition": "Condition A",
      "likelihood": "Less Likely",
      "urgent": false,
      "rationale": "Brief reasoning",
      "reasoning": "Clinical reasoning chain for this diagnosis",
      "supporting_factors": ["Supporting factor 1", "Supporting factor 2"]
    }
  ],
  "red_flags": ["warning sign requiring immediate attention"],
  "clarification_items": [
    {
      "question": "Question to clarify or refine the differential?",
      "pertains_to_conditions": ["Exact condition label from primary_diagnoses or alternative_diagnoses"],
      "refinement_aspect": "context"
    }
  ],
  "next_step_items": [
    {
      "step": "Recommended next step (test, referral, monitoring, etc.)",
      "pertains_to_conditions": ["Exact condition label when this step discriminates or rules out a diagnosis"],
      "refinement_aspect": "diagnostics"
    }
  ]
}

CLARIFICATIONS & REFINEMENTS: Use "clarification_items" only (not a parallel string list). Each item must have "question". When you can infer it, set "refinement_aspect" to one of: "context", "history", "exam", "diagnostics" (required in your reasoning when clinically clear). List every diagnosis label you are discriminating in "pertains_to_conditions" using the exact spelling from your differential. Cover context, history, exam, and diagnostics as needed for safe refinement. List acute and can't-miss differentiators first in the JSON array as a soft guardrail; the client re-sorts by acuity.

NEXT STEPS: Use "next_step_items" with "step" (string) and optional "pertains_to_conditions" / "refinement_aspect" (usually "diagnostics"). Legacy "next_steps" string arrays are discouraged.

LIKELIHOOD CATEGORIZATION: Assign each diagnosis exactly one of: "More Likely", "Likely", "Less Likely".
URGENT FLAG: For EACH diagnosis, set "urgent": true or false (JSON boolean).`,

  'chartmind-ddx-final': `Return ONLY valid JSON - no markdown, no code fences, no explanatory text.
Include ALL diagnoses from the original list (updated as needed).
Include ALL new diagnoses derived from test results.
Include the "potentially_missed_high_risk" array (can be empty []).
All boolean values must be true or false (not strings).
Do not truncate - complete the entire JSON response.

OUTPUT FORMAT:
{
  "primary_diagnoses": [
    {
      "condition": "Diagnosis Name",
      "likelihood": "More Likely",
      "urgent": true,
      "rationale": "Brief summary including clinical context and test result impact",
      "reasoning": "Demographics/PMH → Symptoms → Vitals/Exam → Test result → Conclusion",
      "supporting_factors": ["Age/sex factor", "PMH factor", "Symptom", "Vital sign", "Exam finding", "Test result"],
      "test_impact": "strongly_supported",
      "re_enabled": false,
      "re_enabled_reason": null
    }
  ],
  "alternative_diagnoses": [],
  "red_flags": ["Any red flags from comprehensive clinical picture"],
  "clarification_items": [],
  "next_step_items": [
    {
      "step": "Recommended next step based on final DDX and test results",
      "pertains_to_conditions": [],
      "refinement_aspect": "diagnostics"
    }
  ],
  "potentially_missed_high_risk": [
    {
      "condition": "Diagnosis that should be considered",
      "trigger": "Why this should be considered (symptom/risk factor match)",
      "recommended_workup": "Suggested test to rule out",
      "urgency": "immediate | urgent | consider"
    }
  ]
}

CLARIFICATIONS & NEXT STEPS: Same contract as chartmind-ddx — use "clarification_items" and "next_step_items" with "pertains_to_conditions" (exact differential labels) and "refinement_aspect" when clinically clear.

FIELD REQUIREMENTS:
- "condition": Diagnosis name (REQUIRED)
- "likelihood": One of "More Likely", "Likely", "Less Likely" (REQUIRED)
- "urgent": Boolean true/false (REQUIRED)
- "rationale": Brief summary (REQUIRED)
- "reasoning": Detailed reasoning chain (REQUIRED)
- "supporting_factors": Array of ALL supporting evidence (REQUIRED)
- "test_impact": One of "strongly_supported", "supported", "contradicted", "unchanged", "new_from_test" (REQUIRED)
- "re_enabled": Boolean (OPTIONAL, default false)
- "re_enabled_reason": String (REQUIRED if re_enabled is true)`,

  'chartmind-diagnostic-tests': `Return ONLY valid JSON - no markdown, no code fences, no extra text.

Return the response in this exact JSON format:
{
  "tests": [
    {
      "name": "Test Name",
      "category": "Laboratory|Imaging|Procedure|Other",
      "rationale": "Brief explanation of why this test is recommended and which diagnoses it helps confirm/rule out",
      "priority": "high|medium|low"
    }
  ],
  "testingStrategy": "A brief 2-3 sentence summary of the overall diagnostic testing approach and rationale"
}`,

  'chartmind-treatment-options': `Return ONLY valid JSON - no markdown, no code fences, no extra text.

{
  "primary_treatment": {"name": "treatment", "rationale": "why", "dosing": "if applicable"},
  "alternatives": [{"name": "treatment", "rationale": "why", "when_to_use": "condition"}],
  "supportive_care": ["recommendations"],
  "contraindications": ["things to avoid"]
}`,

  'chartmind-discharge-plan': `Generate your response as a structured text document with clearly labeled sections.

The discharge plan must include ALL of these sections:
1. DIAGNOSIS - Patient-friendly explanation of the condition
2. MEDICATIONS - All medications with clear dosing instructions
3. FOLLOW_UP - When and with whom to follow up
4. RETURN_PRECAUTIONS - When to return to emergency care
5. ACTIVITY - Activity restrictions if any
6. DIET - Dietary recommendations if applicable

Use simple language that patients can understand. Avoid medical jargon.`,

  'chartmind-clarifying-questions': `Return ONLY valid JSON - no markdown, no code fences, no extra text.

{
  "clarifying_questions": ["Question 1?", "Question 2?"],
  "red_flags": ["Red flag 1", "Red flag 2"],
  "missing_information": ["Info that would help"]
}`,

  'chartmind-clarifying-questions-ddx-plus': `Return ONLY valid JSON - no markdown, no code fences, no extra text.

{
  "clarification_items": [
    {
      "question": "Question to clarify or refine the differential?",
      "pertains_to_conditions": ["Exact condition label from the EXISTING DIFFERENTIAL section"],
      "refinement_aspect": "context"
    }
  ],
  "red_flags": ["Optional urgent warning signs to monitor"]
}

DDX-PLUS: The encounter text may include a \`[DDX-PLUS: KB-grounded context ...]\` block. Follow the same priority as chartmind-ddx: verified KB edges and can't-miss findings outweigh unverified retrieval and parametric knowledge. Generate clarification questions that help discriminate acute and can't-miss diagnoses first; use pertains_to_conditions with exact labels from the differential provided in the input.

CLARIFICATIONS: Use "clarification_items" only (same contract as chartmind-ddx): each item has "question"; when clinically clear set "refinement_aspect" to one of: "context", "history", "exam", "diagnostics".`,

  'chartmind-chart-generation': `Generate a complete, professional clinical note as valid JSON with clearly labeled sections.

CRITICAL REQUIREMENTS:
1. ABSOLUTE PROHIBITION: You must NEVER include any fictional, fabricated, or assumed information. Only document what is explicitly stated or clearly demonstrated in the transcript.
2. For any section or subsection not documented in the transcript, leave it EMPTY after the colon (e.g., "HEENT:" with nothing after). Do NOT write "Not documented" or any placeholder text.
3. Do NOT add symptoms, findings, diagnoses, or any clinical information that was not explicitly mentioned.
4. Physical Exam follows the SAME empty-data rule: leave undocumented systems empty after the colon. Do NOT fill in presumptive normal findings.
5. Set physicalExamPresumptive to true when NONE of the physical exam systems were documented; false when at least one system has documented findings.`,

  'chartmind-required-sections-tracker': `Return ONLY valid JSON - no markdown, no code fences, no extra text. Use this format:
{
  "sections": [
    {
      "key": "section_key",
      "detected": true,
      "confidence": 85,
      "evidence": "brief quote or null"
    }
  ]
}

IMPORTANT RULES:
- The "detected" field MUST be a boolean (true or false) for EACH section
- Be thorough but conservative - only mark as detected (true) if there is clear, relevant information in the transcript
- If a section is not detected, set detected: false, confidence: 0, and evidence: null
- Confidence should reflect how certain you are that the section has been adequately addressed
- Evidence should be a brief, direct quote or summary from the transcript when detected is true
- You MUST return a response for EVERY section listed, no exceptions
- The "key" field must exactly match the section key provided in the "REQUIRED SECTIONS TO CHECK" list`,

  'chartmind-test-smart-choices': `Return ONLY valid JSON - no markdown, no code fences, no extra text. Use this exact format:
{
  "choices": ["Choice 1", "Choice 2", "Choice 3", "Choice 4", "Choice 5"],
  "unit": "optional unit if applicable (e.g., 'mg/dL', 'mmol/L')",
  "notes": "optional brief clinical note about interpreting these results"
}`,

  'chartmind-clinical-guidance': `Return ONLY valid JSON - no markdown, no code fences, no extra text. Use this structure:
{
  "differentialDiagnoses": [
    {"condition": "Diagnosis name", "likelihood": 70, "rationale": "Brief explanation"}
  ],
  "suggestedQuestions": ["Question 1?", "Question 2?"],
  "redFlags": ["Red flag 1", "Red flag 2"],
  "recommendations": ["Recommendation 1", "Recommendation 2"]
}`,

  'chartmind-translate': `Return ONLY the translated text—no commentary, labels, or JSON. Translate strictly what the user supplied as text to translate: do not add words, complete thoughts, or fabricate clinical content not present in the source.`,

  'chartmind-format-physical-exam': `Return ONLY the reformatted physical exam text. No markdown, no code fences, no explanation. Each line must be "SectionName: findings" with one system per line.`,

  'chartmind-peerview-consultation': `Format your responses in a clear, professional manner suitable for physician-to-physician consultation. Be concise but thorough. Reference specific details from the case context when relevant.`,
  'chartmind-v2-ddx': `Return ONLY valid JSON - no markdown, no code fences, no extra text.
Use the chartmind-ddx schema with primary_diagnoses, alternative_diagnoses, red_flags, clarification_items, and next_step_items.
Return 3-5 primary_diagnoses and 4-7 alternative_diagnoses (7-12 total across both arrays).
Rank all diagnoses from most likely to least likely within each array (most likely first in primary_diagnoses, then in alternative_diagnoses).`,
  'chartmind-v2-ddx-final': `Return ONLY valid JSON - no markdown, no code fences, no extra text.
Use the chartmind-ddx-final schema and include test impact metadata when applicable.`,
  'chartmind-v2-clarifying-questions': `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "clarification_items": [
    {
      "question": "Question text",
      "pertains_to_conditions": ["Condition label"],
      "refinement_aspect": "context"
    }
  ],
  "red_flags": []
}`,
  'chartmind-v2-diagnostic-tests': `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "tests": [
    {
      "name": "Test Name",
      "category": "Laboratory|Imaging|Procedure|Other",
      "rationale": "Why this test is recommended and what it confirms or rules out",
      "priority": "high|medium|low",
      "pertains_to_conditions": ["Exact condition label from the current differential that this test helps confirm or rule out"]
    }
  ],
  "testingStrategy": "Brief summary of the overall diagnostic testing approach"
}`,
  'chartmind-v2-treatment-ed': `Return ONLY valid JSON - no markdown, no code fences, no extra text.
Use the chartmind-treatment-options schema.`,
  'chartmind-v2-treatment-pc': `Return ONLY valid JSON - no markdown, no code fences, no extra text.
Use the chartmind-treatment-options schema.`,
  'chartmind-v2-treatment-uc': `Return ONLY valid JSON - no markdown, no code fences, no extra text.
Use the chartmind-treatment-options schema.`,
  'chartmind-v2-chart-generation-ed': `Generate a complete, professional clinical note as valid JSON with clearly labeled sections. Return JSON only.`,
  'chartmind-v2-chart-generation-pc': `Generate a complete, professional clinical note as valid JSON with clearly labeled sections. Return JSON only.`,
  'chartmind-v2-chart-generation-uc': `Generate a complete, professional clinical note as valid JSON with clearly labeled sections. Return JSON only.`,
  'chartmind-v2-discharge-plan': `Generate patient-friendly discharge instructions in structured text sections. Return plain text only.`,
  'chartmind-v2-peerview-consultation': `Format your response as concise physician-to-physician consultation guidance.`,
  'chartmind-v2-format-physical-exam': `Return ONLY the reformatted physical exam text. No markdown or commentary.`,
  'chartmind-v2-translate': `Return ONLY the translated text—no commentary, labels, or JSON.`,

  'securelink-conversation': `Return ONLY valid JSON - no markdown, no code fences, no extra text.

When the conversation is NOT yet complete, return:
{
  "isComplete": false,
  "nextQuestion": "Your next question to the patient",
  "findingsSoFar": [
    { "label": "Topic label", "value": "Patient's response summary" }
  ]
}

When the conversation IS complete (all goals covered or maxQuestions reached), return:
{
  "isComplete": true,
  "closingMessage": "A warm, natural wrap-up statement thanking the patient and letting them know their information will be shared with their provider",
  "findings": [
    { "label": "Topic label", "value": "Patient's response summary" }
  ],
  "summary": "Brief narrative summary of all findings in 2-3 sentences"
}

RULES:
- Do not ask the patient to confirm a symptom, finding, or detail that they already stated in this conversation; ask about something new that advances the intake
- Each finding should have a clear, concise label and a value summarizing what the patient reported
- If the patient denied a symptom, record it (e.g., label: "Floaters", value: "Denies")
- The summary should be a brief clinical-style narrative suitable for a provider to read quickly
- The closingMessage MUST be included when isComplete is true — it is spoken aloud to the patient, so make it warm and conversational (e.g., "Thank you so much for sharing that information! Your provider will review everything before your appointment. You're all set.")
- When QUESTIONS_ASKED_SO_FAR equals or exceeds MAX_QUESTIONS, you MUST set isComplete to true and wrap up — do not ask another question
- Always return valid JSON with no trailing commas`,

  'conversation-template-question-suggester': `Return ONLY a valid JSON array of question strings - no markdown, no code fences, no extra text.

Example:
["What symptoms are you experiencing?", "When did they start?", "Have you tried any treatments?"]

RULES:
- Return 5-8 concise, clinically appropriate seed questions
- Each item must be a plain string ending with a question mark
- Questions should help cover the clinician's stated conversation goals
- Do not wrap the array in an object`,

  'securelink-ros-extraction': `Return ONLY valid JSON - no markdown, no code fences, no extra text.

You will receive intake conversation transcript and/or findings. Extract Review of Systems (ROS) into exactly 10 systems. Return a JSON array of 10 objects, one per system, in this exact order:

[
  { "system": "Constitutional", "value": "brief summary or 'No complaints'" },
  { "system": "HEENT", "value": "..." },
  { "system": "Cardiovascular", "value": "..." },
  { "system": "Respiratory", "value": "..." },
  { "system": "GI", "value": "..." },
  { "system": "GU", "value": "..." },
  { "system": "Musculoskeletal", "value": "..." },
  { "system": "Neurological", "value": "..." },
  { "system": "Psychiatric", "value": "..." },
  { "system": "Skin", "value": "..." }
]

RULES:
- Use exactly these system names (spelling and order). If the transcript does not mention a system, use value "No complaints" or "Not discussed".
- Keep each value concise (one short sentence or "No complaints").
- Return only the JSON array, no other text.`,

  'ddx-temporal-extraction': `Return ONLY valid JSON - no markdown, no code fences, no preamble.

Schema:
{
  "acuityEstimate": "hyperacute" | "acute" | "subacute" | "chronic" | "indeterminate",
  "temporalRedFlags": ["string"],
  "keyFeatures": [
    {
      "featureId": "known KB feature id or null",
      "rawLabel": "string",
      "featureType": "symptom",
      "featureStatus": "present" | "absent" | "resolved" | "historical" | "unknown",
      "onsetPattern": "sudden" | "rapid" | "gradual" | "insidious" | null,
      "durationBucket": "hours_to_day or similar",
      "temporalContext": "string",
      "confidence": 0.0
    }
  ],
  "demographics": { "ageBand": "pediatric"|"young_adult"|"adult"|"elderly"|null, "sex": "male"|"female"|null, "ageSpecific": 0 },
  "patientContext": {
    "ageBand": "pediatric"|"young_adult"|"adult"|"elderly"|null,
    "ageSpecific": null,
    "biologicalSex": "male"|"female"|"unknown",
    "smokingStatus": "never"|"former"|"current_light"|"current_heavy"|"unknown",
    "packYears": null,
    "alcoholUse": "none"|"moderate"|"heavy"|"binge"|"unknown",
    "drugUse": ["cocaine","stimulants","opioids","cannabis","ivdu","other"],
    "housingStatus": "housed"|"unstable"|"unhoused"|"incarcerated"|"unknown",
    "occupationRisk": "none_known"|"healthcare"|"farming"|"construction"|"mining"|"food_service"|"sex_work"|"first_responder"|"unknown",
    "sexualHistoryFlags": ["multiple_partners","new_partner","msm","unprotected","not_disclosed"],
    "immunosuppressionStatus": "none"|"hiv_controlled"|"hiv_aids"|"transplant"|"active_chemo"|"chronic_steroids"|"biologic_therapy"|"primary_immunodef"|"other"|"unknown",
    "pregnancyStatus": "not_pregnant"|"pregnant_first"|"pregnant_second"|"pregnant_third"|"postpartum"|"unknown"|"not_applicable",
    "anticoagulationStatus": "none"|"warfarin"|"doac"|"heparin"|"antiplatelet"|"unknown",
    "obesityStatus": "normal_or_unknown"|"overweight"|"obese"|"severely_obese"|"post_bariatric",
    "relevantComorbidities": ["cad","malignancy","prior_pe_dvt"],
    "familyHistoryFlags": [],
    "highImpactMedications": ["ocp","ace_inhibitors","digoxin"],
    "vaccinationGaps": [],
    "priorEpisodeStatus": "first_episode"|"recurrent"|"known_chronic"|"unknown",
    "travelHistory": "none"|"domestic"|"tropical"|"endemic_tb"|"endemic_zika"|"endemic_hanta"|"endemic_coccidioides"|"unknown",
    "mechanismOfInjury": "none"|"low_energy"|"high_energy"|"penetrating"|"blast"|"unknown",
    "confidences": [ { "factor": "string", "confidence": 0.0, "evidence": "short excerpt" } ],
    "flaggedHighImpactFactors": ["immunosuppressionStatus","pregnancyStatus","drugUse"],
    "activeContextWarnings": ["human-readable strings"]
  }
}

Rules:
- Extract all mentioned symptoms, history, exam, and diagnostics.
- featureStatus: resolved = was present and now gone; absent = explicitly denied; unknown = not mentioned. NEVER map resolved to absent.
- Flag temporal red flags (sudden onset, worst of life, progressive worsening).
- Match featureId to KNOWN_FEATURE_IDS_JSON hints when applicable.

PATIENT CONTEXT (patientContext object):
- If a factor is not mentioned or strongly implied, use "unknown" for scalars or [] for arrays. Never infer from silence.
- "none" means explicitly absent; "unknown" means not discussed. Exception: biologicalSex may be inferred from pronouns or clear clinical context.
- immunosuppressionStatus and pregnancyStatus default to "unknown" when not stated (never default to "none").
- Map anticoagulants: warfarin/coumadin→warfarin; apixaban/eliquis, rivaroxaban/xarelto, dabigatran/pradaxa, edoxaban/savaysa→doac; aspirin, clopidogrel/plavix, ticagrelor/brilinta, prasugrel→antiplatelet; heparin, enoxaparin/lovenox (therapeutic)→heparin.
- flaggedHighImpactFactors: list factor field names that RESHAPES_DIFFERENTIAL (e.g. immunosuppression other than none, pregnancy/postpartum, ivdu/cocaine, malignancy or prior_pe_dvt, anticoagulation if bleeding context, non-domestic travel, unhoused/incarcerated).
- Include confidences[] with evidence excerpts for any factor with confidence < 0.80.`,

  'ddx-chief-complaint-resolution': `Return ONLY valid JSON - no markdown, no code fences, no preamble.

Schema:
{
  "candidates": [
    {
      "ccId": "cc_chest_pain",
      "label": "Chest pain",
      "confidence": 0.0,
      "resolutionPath": "exact | synonym | semantic | feature_inferred | llm"
    }
  ]
}

Rules:
- Map messy clinical language to one or more KB chief complaint ids (cc_*).
- If multiple distinct complaints are present, return all of them with confidence 0.0-1.0 each.
- Do NOT collapse two genuinely distinct complaints into a single ccId.
- Return JSON only.

CONTEXT-INFORMED RESOLUTION (use PATIENT_CONTEXT_JSON when provided):
- Immunosuppression not none/unknown: broaden fever/systemic CC candidates.
- Pregnancy or postpartum: weight obstetric and related CCs even if chief narrative is non-specific.
- drugUse includes ivdu: weight infectious CCs (endocarditis, sepsis, epidural abscess) for systemic presentations.
- mechanismOfInjury high_energy: bias toward trauma CCs when history is incomplete.`,

  'ddx-source-extraction': `Return ONLY valid JSON — no markdown, no code fences, no preamble.

{
  "diagnoses": [ { "id": "string|null", "label": "string", "snomedCode": "string|null", "provisionalScore": 0.0 } ],
  "edges": [ { "diagnosisId": "string", "featureId": "string", "edgeStrength": 0.0, "clinicalSignificance": "rule_in|discriminator|..." } ],
  "extractionConfidence": 0.0
}

Rules:
- Input JSON includes searchResults[] (title, link, snippet) from allowlisted domains only.
- Extract diagnoses, clinical features, and relationships ONLY as stated in those snippets. Never fabricate.
- For each edge, estimate strength from language (e.g. pathognomonic ~0.95, occasionally ~0.3).
- If searchResults is empty or uninformative, return empty diagnoses and edges and extractionConfidence 0.`,

  'ddx-discrepancy-detection': `Return ONLY valid JSON — no markdown, no code fences.

{
  "discrepancies": [
    {
      "discrepancyType": "strength_disagreement|missing_edge|extra_edge|temporal_disagreement|cant_miss_disagreement|wording_only",
      "severity": "critical|moderate|low",
      "explanation": "string",
      "suggestedAction": "string"
    }
  ]
}

Distinguish wording-only differences from true clinical disagreement. Prefer wording_only when appropriate.`,
};

// Query Manager / Firestore: stored prompts often override these RESPONSE_FORMATS fallbacks in production.
// When updating DDx contracts, align stored schemas for chartmind-ddx, chartmind-ddx-final, and symptom-checker-ddx
// (plus standalone / phase3 variants) to use clarification_items and next_step_items as documented above.

const DDX_TEMPORAL_EXTRACTION_SYSTEM_PROMPT = `You are a clinical NLP extractor for emergency-medicine triage. Read the clinical transcript and output JSON only (see response format). Preserve the distinction between resolved symptoms and denied (absent) symptoms. After temporal features, extract patientContext with conservative defaults (unknown / empty arrays) for unstated factors.`;

const DDX_CHIEF_COMPLAINT_RESOLUTION_SYSTEM_PROMPT = `You map free-text chief complaints to HealthDesk DDX-Plus chief complaint registry ids (cc_*). You may receive PATIENT_CONTEXT_JSON alongside the transcript—use it to broaden or re-weight CC candidates when clinically appropriate. Output JSON only per response format. When language is ambiguous, return multiple ranked candidates with honest confidence scores.`;

const DDX_SOURCE_EXTRACTION_SYSTEM_PROMPT = `You extract structured DDX-Plus graph candidates from short search snippets for human verification. Never invent citations or relationships not supported by the snippets. Output JSON only per response format.`;

const DDX_DISCREPANCY_DETECTION_SYSTEM_PROMPT = `You compare KB snippets vs source snippets for clinical discrepancies. Output JSON only per response format.`;

// ============================================
// QUERY METADATA REGISTRY (no prompt text)
// This maps query IDs to their fallback prompts
// No P1 prompt text in codebase. P1 prompts: Secret Manager (prod) or P1_PROMPTS_DIR (dev).
// ============================================

export const QUERY_METADATA = {
  // Symptom Checker (Patient-facing)
  'symptom-checker-ddx': {
    id: 'symptom-checker-ddx',
    featureName: 'Symptom Checker - Differential Diagnosis',
    featureDescription: 'Analyzes patient symptoms to generate differential diagnoses with patient-friendly language',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[PATIENT MEDICAL HISTORY]**: Patient's medical records if available (demographics, PMH, PSH, medications, allergies, social/family history)
- **[CLINICIAN SPECIALTY CONTEXT]**: Specialty-specific guidance (if present)
- **[existing transcript]**: The complete conversation history with the patient so far
- **[existing DDX list]**: Previously generated differential diagnoses (if present)

**PREREQUISITE INFORMATION ALREADY COLLECTED:**
Before you receive the transcript, the system has already collected through structured prerequisite questions:
- Patient's age and sex (combined question)
- Duration of their chief complaint (e.g., "How long have you been experiencing the chest pain?")
- Any associated symptoms the patient is experiencing
- Duration of associated symptoms (if applicable)

This information will be in the transcript. Do NOT re-ask these questions.

**DDX-PLUS (when enabled)**: The input may include a trailing \`[DDX-PLUS: KB-grounded context ...]\` block with Tier 1 KB snippets, explicit can’t-miss diagnoses to surface, and expected-but-missing features — use it as adjunct grounding, not as a definitive diagnosis.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'DDXVisualization',
      file: 'features/patient/components/symptomCheck/ddx/useDifferentialDiagnosis.js',
    },
  },

  'symptom-checker-ddx-standalone': {
    id: 'symptom-checker-ddx-standalone',
    featureName: 'Symptom Checker (Standalone) - Differential Diagnosis',
    featureDescription: 'Same as symptom-checker-ddx; used when entry point is standalone. Prompt nudges user toward creating My HealthDesk / enrolling as patient when appropriate.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[PATIENT MEDICAL HISTORY]**: Patient's medical records if available (demographics, PMH, PSH, medications, allergies, social/family history)
- **[CLINICIAN SPECIALTY CONTEXT]**: Specialty-specific guidance (if present)
- **[existing transcript]**: The complete conversation history with the patient so far
- **[existing DDX list]**: Previously generated differential diagnoses (if present)

**PREREQUISITE INFORMATION ALREADY COLLECTED:**
Before you receive the transcript, the system has already collected through structured prerequisite questions:
- Patient's age and sex (combined question)
- Duration of their chief complaint (e.g., "How long have you been experiencing the chest pain?")
- Any associated symptoms the patient is experiencing
- Duration of associated symptoms (if applicable)

This information will be in the transcript. Do NOT re-ask these questions.

**DDX-PLUS (when enabled)**: The input may include a trailing \`[DDX-PLUS: KB-grounded context ...]\` block with Tier 1 KB snippets, explicit can’t-miss diagnoses to surface, and expected-but-missing features — use it as adjunct grounding, not as a definitive diagnosis.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'DDXVisualization',
      file: 'features/patient/components/symptomCheck/ddx/useDifferentialDiagnosis.js',
    },
  },
  
  'symptom-checker-ddx-genetic-rare': {
    id: 'symptom-checker-ddx-genetic-rare',
    featureName: 'Symptom Checker - Genetic/Rare Additive Differential',
    featureDescription: 'Additive genetic/rare-only query for symptom checker. Uses transcript context and returns only plausible genetic/rare possibilities.',
    category: 'symptom_checker',
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[existing transcript]**: The complete conversation history with the patient so far
- **[existing DDX list]**: Previously generated differential diagnoses (if present)

Task:
- Return only genetic or rare conditions that might reasonably match the transcript.
- If no plausible genetic/rare conditions are supported, return an empty diagnoses list.
- Use the same JSON schema as symptom-checker-ddx.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: {
      component: 'DDXVisualization',
      file: 'features/patient/components/symptomCheck/ddx/useDifferentialDiagnosis.js',
    },
  },

  'symptom-checker-ddx-genetic-rare-standalone': {
    id: 'symptom-checker-ddx-genetic-rare-standalone',
    featureName: 'Symptom Checker (Standalone) - Genetic/Rare Additive Differential',
    featureDescription: 'Standalone additive genetic/rare-only query for symptom checker.',
    category: 'symptom_checker',
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[existing transcript]**: The complete conversation history with the patient so far
- **[existing DDX list]**: Previously generated differential diagnoses (if present)

Task:
- Return only genetic or rare conditions that might reasonably match the transcript.
- If no plausible genetic/rare conditions are supported, return an empty diagnoses list.
- Use the same JSON schema as symptom-checker-ddx.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: {
      component: 'DDXVisualization',
      file: 'features/patient/components/symptomCheck/ddx/useDifferentialDiagnosis.js',
    },
  },
  
  'symptom-checker-ddx-phase3': {
    id: 'symptom-checker-ddx-phase3',
    featureName: 'Symptom Checker (Phase 3) - DSCR Factor Re-query',
    featureDescription: 'Phase 3 Approach C prompt. Refines the DDX using confirmed/denied clinical factors from published scoring tools. Transcript is prepended with a [FACTOR ASSESSMENT] block.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[FACTOR ASSESSMENT]**: Confirmed and denied clinical factors from published scoring tools, with weights and tool names
- **[PATIENT MEDICAL HISTORY]**: Patient's medical records if available
- **[CLINICIAN SPECIALTY CONTEXT]**: Specialty-specific guidance (if present)
- **[existing transcript]**: The complete conversation history with the patient
- **[existing DDX list]**: Previously generated differential diagnoses

**PHASE 3 NOTE:** The patient has already been through Phase 1 (symptom discovery) and Phase 2 (LLM-driven clarifications with DDX). This is the evidence refinement phase using published clinical scoring tools.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: {
      component: 'DDXVisualization',
      file: 'features/patient/components/symptomCheck/ddx/useDifferentialDiagnosis.js',
    },
  },

  'symptom-checker-ddx-standalone-phase3': {
    id: 'symptom-checker-ddx-standalone-phase3',
    featureName: 'Symptom Checker (Standalone Phase 3) - DSCR Factor Re-query',
    featureDescription: 'Same as symptom-checker-ddx-phase3; used when entry point is standalone. Locked for standalone users initially but ready for future use.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[FACTOR ASSESSMENT]**: Confirmed and denied clinical factors from published scoring tools, with weights and tool names
- **[PATIENT MEDICAL HISTORY]**: Patient's medical records if available
- **[CLINICIAN SPECIALTY CONTEXT]**: Specialty-specific guidance (if present)
- **[existing transcript]**: The complete conversation history with the patient
- **[existing DDX list]**: Previously generated differential diagnoses

**PHASE 3 NOTE:** The patient has already been through Phase 1 (symptom discovery) and Phase 2 (LLM-driven clarifications with DDX). This is the evidence refinement phase using published clinical scoring tools.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: {
      component: 'DDXVisualization',
      file: 'features/patient/components/symptomCheck/ddx/useDifferentialDiagnosis.js',
    },
  },

  'symptom-checker-v2-ddx': {
    id: 'symptom-checker-v2-ddx',
    featureName: 'Symptom Checker 2 - Differential Diagnosis',
    featureDescription: 'Category-driven DDx for dashboard flow. Interprets bracketed category blocks in user input.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with bracketed category blocks in the user message:
- [CLINICAL SETTING: ...]
- [PATIENT: ...]
- [HISTORY: ...]
- [EXAM & DIAGNOSTICS: ...]

Interpret these blocks as structured clinical context. Use all available blocks, and preserve previously surfaced important differentials unless clearly contradicted by new evidence.

**DDX-PLUS (when enabled)**: The input may include a trailing \`[DDX-PLUS: KB-grounded context ...]\` block with Tier 1 KB snippets, explicit can’t-miss diagnoses to surface, and expected-but-missing features.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    // TODO: update Firestore llmQueries/{queryId} document to add clarifying_questions
    // to responseFormat and prompt — llmQueryMetadata.js is NOT the runtime source
    // for Firestore-managed query ids (see QUERY_IDS_FIRESTORE_MANAGED_PROMPT).
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "diagnoses": [
    {
      "condition": "Condition name",
      "urgencyTier": "urgent|soon|routine",
      "rationale": "Plain explanation appropriate for a patient"
    }
  ],
  "clarifying_questions": [
    "Plain English question the patient can answer to help clarify their situation"
  ]
}`,
    uiLocation: {
      component: 'SymptomCheckV2Page',
      file: 'features/patient/components/symptomCheckV2/SymptomCheckV2Page.jsx',
    },
  },

  'symptom-checker-v2-ddx-standalone': {
    id: 'symptom-checker-v2-ddx-standalone',
    featureName: 'Symptom Checker 2 (Standalone) - Differential Diagnosis',
    featureDescription: 'Category-driven DDx for standalone flow; same schema as dashboard v2 query.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with bracketed category blocks in the user message:
- [CLINICAL SETTING: ...]
- [PATIENT: ...]
- [HISTORY: ...]
- [EXAM & DIAGNOSTICS: ...]

Interpret these blocks as structured clinical context. Use all available blocks, and preserve previously surfaced important differentials unless clearly contradicted by new evidence.

**DDX-PLUS (when enabled)**: The input may include a trailing \`[DDX-PLUS: KB-grounded context ...]\` block with Tier 1 KB snippets, explicit can’t-miss diagnoses to surface, and expected-but-missing features.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    // TODO: update Firestore llmQueries/{queryId} document to add clarifying_questions
    // to responseFormat and prompt — llmQueryMetadata.js is NOT the runtime source
    // for Firestore-managed query ids (see QUERY_IDS_FIRESTORE_MANAGED_PROMPT).
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "diagnoses": [
    {
      "condition": "Condition name",
      "urgencyTier": "urgent|soon|routine",
      "rationale": "Plain explanation appropriate for a patient"
    }
  ],
  "clarifying_questions": [
    "Plain English question the patient can answer to help clarify their situation"
  ]
}`,
    uiLocation: {
      component: 'SymptomCheckV2Page',
      file: 'features/patient/components/symptomCheckV2/SymptomCheckV2Page.jsx',
    },
  },

  'symptom-checker-v2-ddx-phase3': {
    id: 'symptom-checker-v2-ddx-phase3',
    featureName: 'Symptom Checker 2 (Phase 3) - DSCR Re-query',
    featureDescription: 'Refines v2 DDx using [FACTOR ASSESSMENT] evidence plus category transcript blocks.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- [FACTOR ASSESSMENT]
- [CLINICAL SETTING: ...]
- [PATIENT: ...]
- [HISTORY: ...]
- [EXAM & DIAGNOSTICS: ...]
- [existing DDX list]

Use factor evidence to refine relative likelihood and urgency while preserving safety-oriented differentials.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    // TODO: update Firestore llmQueries/{queryId} document to align phase-3 responseFormat
    // and prompt (diagnoses only) — llmQueryMetadata.js is NOT the runtime source
    // for Firestore-managed query ids (see QUERY_IDS_FIRESTORE_MANAGED_PROMPT).
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "diagnoses": [
    {
      "condition": "Condition name",
      "urgencyTier": "urgent|soon|routine",
      "rationale": "Plain explanation appropriate for a patient"
    }
  ]
}`,
    uiLocation: {
      component: 'SymptomCheckV2Page',
      file: 'features/patient/components/symptomCheckV2/SymptomCheckV2Page.jsx',
    },
  },

  'symptom-checker-v2-ddx-standalone-phase3': {
    id: 'symptom-checker-v2-ddx-standalone-phase3',
    featureName: 'Symptom Checker 2 (Standalone Phase 3) - DSCR Re-query',
    featureDescription: 'Standalone v2 phase-3 refinement query using bracketed categories + factor assessment.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- [FACTOR ASSESSMENT]
- [CLINICAL SETTING: ...]
- [PATIENT: ...]
- [HISTORY: ...]
- [EXAM & DIAGNOSTICS: ...]
- [existing DDX list]

Use factor evidence to refine relative likelihood and urgency while preserving safety-oriented differentials.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    // TODO: update Firestore llmQueries/{queryId} document to align phase-3 responseFormat
    // and prompt (diagnoses only) — llmQueryMetadata.js is NOT the runtime source
    // for Firestore-managed query ids (see QUERY_IDS_FIRESTORE_MANAGED_PROMPT).
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "diagnoses": [
    {
      "condition": "Condition name",
      "urgencyTier": "urgent|soon|routine",
      "rationale": "Plain explanation appropriate for a patient"
    }
  ]
}`,
    uiLocation: {
      component: 'SymptomCheckV2Page',
      file: 'features/patient/components/symptomCheckV2/SymptomCheckV2Page.jsx',
    },
  },

  'symptom-checker-v2-triage': {
    id: 'symptom-checker-v2-triage',
    featureName: 'SC v2 Care Guidance & Diagnostic Need Assessment',
    featureDescription:
      'Given completed v2 DDX, returns: care setting recommendation, implied diagnostic capability needs as structured array, plain-language action guidance for the patient.',
    category: 'symptom_checker',
    contextProvided: `# CONTEXT PROVIDED

You will be provided with a completed v2 differential diagnosis (DDX) result, the patient's symptom conversation transcript, and optional patient context. Use it to recommend a care setting, the diagnostic tests/capabilities the visit may require, an estimate of symptom duration in days, and plain-language next-step guidance for the patient.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 2048,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    outputFormat: 'json',
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "careLevel": "ER | UrgentCare | PrimaryCare | Specialist",
  "diagnosticNeeds": ["xray", "cbc"],
  "symptomDurationDays": 0,
  "actionGuidance": "Direct action sentence in second person present tense.",
  "confidence": 0.0
}`,
    uiLocation: {
      component: 'SCMapPage',
      file: 'pages/pages/scmap/SCMapPage.jsx',
    },
  },

  'symptom-checker-v2-triage-standalone': {
    id: 'symptom-checker-v2-triage-standalone',
    featureName: 'SC v2 Care Guidance & Diagnostic Need Assessment (Standalone)',
    featureDescription:
      'Standalone /scmap variant. Given completed v2 DDX, returns: care setting recommendation, implied diagnostic capability needs as structured array, plain-language action guidance for the patient.',
    category: 'symptom_checker',
    contextProvided: `# CONTEXT PROVIDED

You will be provided with a completed v2 differential diagnosis (DDX) result, the patient's symptom conversation transcript, and optional patient context. Use it to recommend a care setting, the diagnostic tests/capabilities the visit may require, an estimate of symptom duration in days, and plain-language next-step guidance for the patient.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 2048,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    outputFormat: 'json',
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "careLevel": "ER | UrgentCare | PrimaryCare | Specialist",
  "diagnosticNeeds": ["xray", "cbc"],
  "symptomDurationDays": 0,
  "actionGuidance": "Direct action sentence in second person present tense.",
  "confidence": 0.0
}`,
    uiLocation: {
      component: 'SCMapPage',
      file: 'pages/pages/scmap/SCMapPage.jsx',
    },
  },

  // /scmap2 isolated fork — metadata mirrors /scmap sources until Firestore docs are cloned.
  'symptom-checker-v2-ddx-standalone-scmap2': {
    id: 'symptom-checker-v2-ddx-standalone-scmap2',
    featureName: 'Symptom Checker 2 (/scmap2) - Standalone DDX',
    featureDescription: 'Isolated /scmap2 fork of standalone v2 DDX; edit prompts without affecting /scmap.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with bracketed category blocks in the user message:
- [CLINICAL SETTING: ...]
- [PATIENT: ...]
- [HISTORY: ...]
- [EXAM & DIAGNOSTICS: ...]

Interpret these blocks as structured clinical context. Use all available blocks, and preserve previously surfaced important differentials unless clearly contradicted by new evidence.

**DDX-PLUS (when enabled)**: The input may include a trailing \`[DDX-PLUS: KB-grounded context ...]\` block with Tier 1 KB snippets, explicit can't-miss diagnoses to surface, and expected-but-missing features.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "diagnoses": [
    {
      "condition": "Condition name",
      "urgencyTier": "urgent|soon|routine",
      "rationale": "Plain explanation appropriate for a patient"
    }
  ],
  "clarifying_questions": [
    "Plain English question the patient can answer to help clarify their situation"
  ]
}`,
    uiLocation: {
      component: 'Scmap2Page',
      file: 'pages/pages/scmap2/Scmap2Page.jsx',
    },
  },

  'symptom-checker-v2-triage-scmap2': {
    id: 'symptom-checker-v2-triage-scmap2',
    featureName: 'SC v2 Care Guidance (/scmap2 fork)',
    featureDescription:
      'Isolated /scmap2 triage query. Given completed v2 DDX, returns care setting, diagnostic needs, duration estimate, and action guidance.',
    category: 'symptom_checker',
    contextProvided: `# CONTEXT PROVIDED

You will be provided with a completed v2 differential diagnosis (DDX) result, the patient's symptom conversation transcript, and optional patient context. Use it to recommend a care setting, the diagnostic tests/capabilities the visit may require, an estimate of symptom duration in days, and plain-language next-step guidance for the patient.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 2048,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    outputFormat: 'json',
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "careLevel": "ER | UrgentCare | PrimaryCare | Specialist",
  "diagnosticNeeds": ["xray", "cbc"],
  "symptomDurationDays": 0,
  "actionGuidance": "Direct action sentence in second person present tense.",
  "confidence": 0.0
}`,
    uiLocation: {
      component: 'Scmap2Page',
      file: 'pages/pages/scmap2/Scmap2Page.jsx',
    },
  },

  'chartmind-diagnostic-tests-scmap2': {
    id: 'chartmind-diagnostic-tests-scmap2',
    featureName: 'ChartMind Diagnostic Tests (/scmap2 fork)',
    featureDescription: 'Isolated /scmap2 fork of DDX→diagnostic test identification.',
    category: 'chartmind',
    ddxPlus: { tier: 'encounter_appendix' },
    responseFormat: RESPONSE_FORMATS['chartmind-diagnostic-tests'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[ENABLED DIAGNOSES LIST]**: A JSON array of differential diagnoses currently enabled by the clinician, each with condition name, likelihood, and rationale. Only enabled (non-excluded) diagnoses are sent.
- **[PATIENT ENCOUNTER TRANSCRIPT]**: The full clinician-patient conversation including chief complaint, symptoms, timeline, and clinical findings discussed
- **[PATIENT DEMOGRAPHICS]**: Age and sex of the patient (critical for test selection, e.g., pregnancy test for women aged 8-55)

The diagnoses list and transcript are appended automatically after the main prompt. Use both the diagnoses AND the clinical presentation to guide test selection — tests should be relevant to the specific presentation, not just generic for the diagnosis.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 1500,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'Scmap2Page',
      file: 'pages/pages/scmap2/Scmap2Page.jsx',
    },
  },

  'symptom-checker-v2-ddx-scmap2': {
    id: 'symptom-checker-v2-ddx-scmap2',
    featureName: 'Symptom Checker 2 (/scmap2) - Dashboard DDX',
    featureDescription: 'Isolated /scmap2 dashboard-phase DDX query id (reserved for future dashboard fork).',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: {
      component: 'Scmap2Page',
      file: 'pages/pages/scmap2/Scmap2Page.jsx',
    },
  },

  'symptom-checker-v2-ddx-standalone-phase3-scmap2': {
    id: 'symptom-checker-v2-ddx-standalone-phase3-scmap2',
    featureName: 'Symptom Checker 2 (/scmap2) - Standalone Phase 3',
    featureDescription: 'Isolated /scmap2 phase-3 refinement query.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: {
      component: 'Scmap2Page',
      file: 'pages/pages/scmap2/Scmap2Page.jsx',
    },
  },

  'symptom-checker-voice-prompts-scmap2': {
    id: 'symptom-checker-voice-prompts-scmap2',
    featureName: 'Symptom Checker Voice Prompts (/scmap2 fork)',
    featureDescription: 'Isolated voice conversation prompts for /scmap2; clone from symptom-checker-voice-prompts.',
    category: 'voice_conversation',
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: {
      component: 'Scmap2Page',
      file: 'pages/pages/scmap2/Scmap2Page.jsx',
    },
  },

  'symptom-checker-v3-ddx': {
    id: 'symptom-checker-v3-ddx',
    featureName: 'Symptom Checker 3 - Differential Diagnosis',
    featureDescription:
      'Voice-first SC3 DDx; same bracketed blocks and JSON contract as SC2. Runtime P1 from llmQueryPrompts (or v2 fallback in Functions).',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with bracketed category blocks in the user message:
- [CLINICAL SETTING: ...]
- [PATIENT: ...]
- [HISTORY: ...]
- [EXAM & DIAGNOSTICS: ...]

Interpret these blocks as structured clinical context. Use all available blocks, and preserve previously surfaced important differentials unless clearly contradicted by new evidence.

**DDX-PLUS (when enabled)**: The input may include a trailing \`[DDX-PLUS: KB-grounded context ...]\` block with Tier 1 KB snippets, explicit can’t-miss diagnoses to surface, and expected-but-missing features.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "diagnoses": [
    {
      "condition": "Condition name",
      "urgencyTier": "urgent|soon|routine",
      "rationale": "Plain explanation appropriate for a patient"
    }
  ],
  "clarifying_questions": [
    "Plain English question the patient can answer to help clarify their situation"
  ]
}`,
    uiLocation: {
      component: 'SymptomCheckV3Page',
      file: 'features/patient/components/symptomCheckV3/SymptomCheckV3Page.jsx',
    },
  },

  'symptom-checker-v3-ddx-standalone': {
    id: 'symptom-checker-v3-ddx-standalone',
    featureName: 'Symptom Checker 3 (Standalone) - Differential Diagnosis',
    featureDescription: 'Standalone SC3 DDx; same schema as dashboard v3 query.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with bracketed category blocks in the user message:
- [CLINICAL SETTING: ...]
- [PATIENT: ...]
- [HISTORY: ...]
- [EXAM & DIAGNOSTICS: ...]

Interpret these blocks as structured clinical context. Use all available blocks, and preserve previously surfaced important differentials unless clearly contradicted by new evidence.

**DDX-PLUS (when enabled)**: The input may include a trailing \`[DDX-PLUS: KB-grounded context ...]\` block with Tier 1 KB snippets, explicit can’t-miss diagnoses to surface, and expected-but-missing features.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "diagnoses": [
    {
      "condition": "Condition name",
      "urgencyTier": "urgent|soon|routine",
      "rationale": "Plain explanation appropriate for a patient"
    }
  ],
  "clarifying_questions": [
    "Plain English question the patient can answer to help clarify their situation"
  ]
}`,
    uiLocation: {
      component: 'SymptomCheckV3Page',
      file: 'features/patient/components/symptomCheckV3/SymptomCheckV3Page.jsx',
    },
  },

  'symptom-checker-v3-ddx-phase3': {
    id: 'symptom-checker-v3-ddx-phase3',
    featureName: 'Symptom Checker 3 (Phase 3) - DSCR Re-query',
    featureDescription: 'Refines SC3 DDx using [FACTOR ASSESSMENT] plus category blocks.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- [FACTOR ASSESSMENT]
- [CLINICAL SETTING: ...]
- [PATIENT: ...]
- [HISTORY: ...]
- [EXAM & DIAGNOSTICS: ...]
- [existing DDX list]

Use factor evidence to refine relative likelihood and urgency while preserving safety-oriented differentials.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "diagnoses": [
    {
      "condition": "Condition name",
      "urgencyTier": "urgent|soon|routine",
      "rationale": "Plain explanation appropriate for a patient"
    }
  ]
}`,
    uiLocation: {
      component: 'SymptomCheckV3Page',
      file: 'features/patient/components/symptomCheckV3/SymptomCheckV3Page.jsx',
    },
  },

  'symptom-checker-v3-ddx-standalone-phase3': {
    id: 'symptom-checker-v3-ddx-standalone-phase3',
    featureName: 'Symptom Checker 3 (Standalone Phase 3) - DSCR Re-query',
    featureDescription: 'Standalone SC3 phase-3 refinement.',
    category: 'symptom_checker',
    ddxPlus: { tier: 'encounter_appendix' },
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- [FACTOR ASSESSMENT]
- [CLINICAL SETTING: ...]
- [PATIENT: ...]
- [HISTORY: ...]
- [EXAM & DIAGNOSTICS: ...]
- [existing DDX list]

Use factor evidence to refine relative likelihood and urgency while preserving safety-oriented differentials.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "diagnoses": [
    {
      "condition": "Condition name",
      "urgencyTier": "urgent|soon|routine",
      "rationale": "Plain explanation appropriate for a patient"
    }
  ]
}`,
    uiLocation: {
      component: 'SymptomCheckV3Page',
      file: 'features/patient/components/symptomCheckV3/SymptomCheckV3Page.jsx',
    },
  },

  'symptom-checker-v4-symptom-details-augment': {
    id: 'symptom-checker-v4-symptom-details-augment',
    featureName: 'Symptom Checker 4 - Symptom Details Augment',
    featureDescription:
      'Generates capped, DDX-relevant supplemental symptom-detail questions for SC4 Symptom Details stage.',
    category: 'symptom_checker',
    prompt: SC4_SYMPTOM_DETAILS_AUGMENT_SYSTEM_PROMPT,
    contextProvided: `# CONTEXT PROVIDED

You will receive:
- chief complaint text
- current transcript so far
- top differential diagnosis labels
- constraints and JSON schema instructions`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 1200,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    responseFormat: `Return ONLY valid JSON - no markdown, no code fences, no extra text.
{
  "questions": [
    {
      "question": "Patient-facing single question ending with ?",
      "relevanceScore": 0.0,
      "targetDiagnoses": ["Diagnosis label from provided differential"],
      "rationale": "Short reason this helps discriminate diagnoses"
    }
  ]
}`,
    uiLocation: {
      component: 'SymptomCheckV4Page',
      file: 'features/patient/components/symptomCheckV4/SymptomCheckV4Page.jsx',
    },
  },

  'symptom-checker-v2-voice-prompts': {
    id: 'symptom-checker-v2-voice-prompts',
    featureName: 'Symptom Checker 2 - Voice Conversation Prompts',
    featureDescription:
      'Global default prompts for SC2 standalone voice flow. Supports optional regional/local override docs.',
    category: 'symptom_checker',
    isStaticContent: true,
    chainConfig: {
      isPartOfChain: true,
      chainId: 'symptom-checker-v2-voice-prompts',
      chainPosition: 1,
    },
    model: 'none',
    temperature: 0,
    maxTokens: 0,
    geographicScope: 'global',
    regions: [],
    localities: [],
    uiLocation: {
      component: 'SymptomCheckV2StandaloneLayout',
      file: 'features/patient/components/symptomCheckV2/SymptomCheckV2StandaloneLayout.jsx',
    },
  },

  'voice-conversation-opening': {
    id: 'voice-conversation-opening',
    featureName: 'Voice Conversation - Opening',
    featureDescription: 'Initial greeting when voice conversation starts',
    category: 'voice_conversation',
    contextProvided: '',
    model: null, // TTS only, not LLM
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'VoiceConversation',
      file: 'features/patient/components/symptomCheck/VoiceConversation.jsx',
    },
  },
  
  'voice-conversation-closing': {
    id: 'voice-conversation-closing',
    featureName: 'Voice Conversation - Closing',
    featureDescription: 'Closing message when voice conversation ends',
    category: 'voice_conversation',
    contextProvided: '',
    model: null, // TTS only, not LLM
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'VoiceConversation',
      file: 'features/patient/components/symptomCheck/VoiceConversation.jsx',
    },
  },
  
  // ChartMind Cards (Professional-facing)
  'chartmind-ddx': {
    id: 'chartmind-ddx',
    featureName: 'ChartMind - Differential Diagnosis',
    featureDescription: 'Professional DDX analysis from encounter transcript',
    category: 'chartmind',
    ddxPlus: { tier: 'encounter_appendix' },
    responseFormat: RESPONSE_FORMATS['chartmind-ddx'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[PATIENT ENCOUNTER TRANSCRIPT]**: The real-time transcription of the clinician-patient conversation, including chief complaint, history of present illness, and any clinical findings discussed
- **[PATIENT MEDICAL HISTORY (CMR)]**: If available from a linked patient record, appended after the transcript:
  - Current Medications (with names)
  - Allergies (with allergen names)
  - Past Medical History (conditions)
  - Past Surgical History (procedures)
  - Demographics (age, gender)
- **[EXISTING DDX LIST]**: If a prior differential diagnosis has already been generated, it will be appended so you can preserve and refine existing diagnoses rather than starting from scratch

**NOTE**: The CMR data may not always be present. If absent, base your analysis solely on the transcript. When CMR data IS present, it appears after a "---" separator with the label "Supplied by patient:".

**CONTRADICTORY PATIENT STATEMENTS**: When the patient appears to report contradictory information in the transcript (e.g. different answers to the same question at different times, such as from an earlier symptom-checker conversation and a later in-person encounter), treat the **last / most recent** version in the transcript as the current, accurate one (e.g. the live encounter overrides earlier pre-filled or symptom-checker content).

**DDX-PLUS (when enabled)**: The user message may end with a block labeled \`[DDX-PLUS: KB-grounded context ...]\`. Treat Tier 1 verified KB content there as structured grounding. The **can’t-miss** subsection lists diagnoses that must be **surfaced explicitly** for clinician consideration (acknowledge, rule out, or work up) — not merely buried in the differential. The **expected-but-missing** subsection lists high-sensitivity features not found in the transcript; use it to suggest focused history or exam questions.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'DDXCard',
      file: 'components/chartmind/cards/cardConfig.js',
    },
  },
  
  'chartmind-ddx-final': {
    id: 'chartmind-ddx-final',
    featureName: 'ChartMind - Final DDX with Test Results',
    featureDescription: 'Updates DDX based on diagnostic test results - adds new diagnoses, reranks likelihoods, updates reasoning',
    category: 'chartmind',
    ddxPlus: { tier: 'encounter_appendix' },
    responseFormat: RESPONSE_FORMATS['chartmind-ddx-final'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with ALL of the following (when available):
- **[PATIENT DEMOGRAPHICS]**: Age, sex, weight
- **[CHIEF COMPLAINT]**: The primary reason for the visit
- **[HISTORY OF PRESENT ILLNESS / ENCOUNTER TRANSCRIPT]**: The full clinical narrative
- **[VITAL SIGNS]**: BP, HR, Temp, RR, SpO2
- **[PAST MEDICAL HISTORY (PMH)]**: Prior diagnoses, chronic conditions
- **[CURRENT MEDICATIONS]**: Full medication list
- **[ALLERGIES]**: Known allergies
- **[PHYSICAL EXAMINATION FINDINGS]**: Documented exam findings
- **[EXISTING DIFFERENTIAL DIAGNOSIS (DDX)]**: JSON list of current diagnoses with likelihoods, reasoning, and user_excluded flags
- **[DIAGNOSTIC TEST RESULTS]**: Results from tests ordered during the encounter

This is the FINAL DDX pass. All available clinical data is compiled into a single comprehensive input. Some diagnoses may be marked "user_excluded: true" — the clinician previously crossed them off. Keep them excluded unless test results strongly contradict the exclusion.

**DDX-PLUS (when enabled)**: If the encounter text includes a \`[DDX-PLUS: KB-grounded context ...]\` block, follow this priority: (1) Tier 1 verified KB — primary reasoning basis; (2) Tier 2 verified — supplement with provenance; (3) Tier 2 unverified / provisional from retrieval — treat as hypothesis, flag to clinician; (4) your parametric knowledge — lowest priority; never override verified KB edges or can't-miss with unverified retrieval. Surface can't-miss items explicitly for clinician action.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'DDXCombinedDisplay',
      file: 'components/chartmind/ddx/DDXCombinedDisplay.jsx',
    },
  },
  
  'chartmind-diagnostic-tests': {
    id: 'chartmind-diagnostic-tests',
    featureName: 'ChartMind - Diagnostic Tests',
    featureDescription: 'Test recommendations based on DDX',
    category: 'chartmind',
    ddxPlus: { tier: 'encounter_appendix' },
    responseFormat: RESPONSE_FORMATS['chartmind-diagnostic-tests'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[ENABLED DIAGNOSES LIST]**: A JSON array of differential diagnoses currently enabled by the clinician, each with condition name, likelihood, and rationale. Only enabled (non-excluded) diagnoses are sent.
- **[PATIENT ENCOUNTER TRANSCRIPT]**: The full clinician-patient conversation including chief complaint, symptoms, timeline, and clinical findings discussed
- **[PATIENT DEMOGRAPHICS]**: Age and sex of the patient (critical for test selection, e.g., pregnancy test for women aged 8-55)

The diagnoses list and transcript are appended automatically after the main prompt. Use both the diagnoses AND the clinical presentation to guide test selection — tests should be relevant to the specific presentation, not just generic for the diagnosis.

**DDX-PLUS (when enabled)**: The user payload may begin with a \`[DDX-PLUS: KB-grounded context ...]\` block appended by the client when DDX-Plus mode is on—use the same prioritization as chartmind-ddx (verified KB and can't-miss items carry more weight than unverified retrieval).`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 1500,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'TestsCard',
      file: 'components/chartmind/cards/cardConfig.js',
    },
  },
  
  'chartmind-treatment-options': {
    id: 'chartmind-treatment-options',
    featureName: 'ChartMind - Treatment Options',
    featureDescription: 'Treatment recommendations for selected diagnosis',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-treatment-options'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[CONFIRMED DIAGNOSIS NAME(S)]**: The diagnosis or diagnoses the clinician has selected/confirmed from the differential diagnosis list
- **[PATIENT ENCOUNTER TRANSCRIPT]**: The full clinician-patient conversation with clinical context
- **[PATIENT MEDICAL HISTORY]**: When available — medications, allergies, comorbidities that affect treatment selection
- **[DDX CONTEXT]**: The full differential diagnosis list may be included to provide clinical context

Treatment recommendations should account for patient-specific factors visible in the transcript and history (e.g., allergies, current medications, comorbidities, patient preferences).`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'TreatmentCard',
      file: 'components/chartmind/cards/cardConfig.js',
    },
  },
  
  'chartmind-discharge-plan': {
    id: 'chartmind-discharge-plan',
    featureName: 'ChartMind - Discharge Plan',
    featureDescription: 'Patient-friendly discharge instructions',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-discharge-plan'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[CONFIRMED DIAGNOSIS NAME(S)]**: The diagnosis or diagnoses confirmed by the clinician
- **[SELECTED TREATMENTS]**: Treatment options the clinician has selected, including medications and dosing
- **[PATIENT ENCOUNTER TRANSCRIPT]**: The full clinical conversation for additional context
- **[PATIENT MEDICAL HISTORY]**: When available — current medications, allergies, relevant comorbidities
- **[LANGUAGE PREFERENCE]**: The discharge plan may need to be generated in a specific language if set by the clinician (default: English)

The discharge plan should be written in patient-friendly language, avoiding medical jargon. All sections (Diagnosis, Medications, Follow-up, Return Precautions, Activity, Diet) must be populated based on the available context.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 2000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'DischargeCard',
      file: 'components/chartmind/cards/cardConfig.js',
    },
  },
  
  'chartmind-clarifying-questions': {
    id: 'chartmind-clarifying-questions',
    featureName: 'ChartMind - Clarifying Questions',
    featureDescription: 'Questions to ask patient and red flags',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-clarifying-questions'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[PATIENT ENCOUNTER TRANSCRIPT]**: The full clinician-patient conversation to date, including chief complaint, symptoms discussed, and clinical findings
- **[EXISTING DIFFERENTIAL DIAGNOSIS]**: The current DDX list with likelihoods — use this to generate questions that would help narrow the differential
- **[PATIENT MEDICAL HISTORY]**: When available — medications, allergies, PMH, PSH for context

Questions should focus on information gaps that would most effectively narrow the differential diagnosis. Red flags should be based on the specific clinical presentation.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 1000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'QuestionsCard',
      file: 'components/chartmind/cards/cardConfig.js',
    },
  },

  'chartmind-clarifying-questions-ddx-plus': {
    id: 'chartmind-clarifying-questions-ddx-plus',
    featureName: 'ChartMind - Clarifying Questions (DDX-Plus)',
    featureDescription: 'KB-grounded clarifying questions when DDX-Plus is active (separate from main DDX response)',
    category: 'chartmind',
    ddxPlus: { tier: 'dedicated_plus_query' },
    responseFormat: RESPONSE_FORMATS['chartmind-clarifying-questions-ddx-plus'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[PATIENT ENCOUNTER TRANSCRIPT]**: The full clinician-patient conversation to date
- **[EXISTING DIFFERENTIAL DIAGNOSIS]**: The current merged DDX list with likelihoods — generate questions that narrow this differential
- **[PATIENT MEDICAL HISTORY]**: When available — medications, allergies, PMH, PSH
- **[DDX-PLUS KB-GROUNDED APPENDIX]**: When present in the transcript payload, treat verified KB context per chartmind-ddx priority rules

This query runs only when DDX-Plus mode is on. Output clarification_items only (structured); do not rely on a parallel legacy string list.`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 1500,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'QuestionsCard',
      file: 'components/chartmind/cards/cardConfig.js',
    },
  },
  
  'chartmind-chart-generation': {
    id: 'chartmind-chart-generation',
    featureName: 'ChartMind - Chart Generation',
    featureDescription: 'Generate complete clinical note from transcript',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-chart-generation'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[PATIENT ENCOUNTER TRANSCRIPT]**: The complete clinician-patient conversation — this is the PRIMARY source for chart generation
- **[PATIENT MEDICAL HISTORY (CMR)]**: When available — demographics, medications, allergies, PMH, PSH, social history, family history
- **[CONFIRMED DIAGNOSES]**: Diagnoses selected by the clinician from the DDX
- **[DIAGNOSTIC TEST RESULTS]**: Any test results entered during the encounter
- **[SELECTED TREATMENTS]**: Treatment options selected by the clinician
- **[REQUIRED SECTIONS LIST]**: A list of chart sections that must be addressed (e.g., Chief Complaint, HPI, ROS, Physical Exam, Assessment, Plan)
- **[LANGUAGE PREFERENCE]**: The chart may need to be generated in a specific language if set by the clinician (default: English)

**CRITICAL**: Only document what is explicitly stated or clearly demonstrated in the transcript. NEVER fabricate or assume information. For any section or subsection not documented in the transcript, leave it empty after the colon — do NOT write "Not documented" or fill in presumptive normal findings. Physical Exam follows this same rule: leave undocumented systems empty.

**CONTRADICTORY PATIENT STATEMENTS**: When the patient appears to report contradictory information in the transcript (e.g. different answers at different times), treat the **last / most recent** version as the current, accurate one (e.g. the live encounter overrides earlier symptom-checker content).`,
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'ChartGeneration',
      file: 'components/chartmind/cards/cardConfig.js',
    },
  },
  
  'chartmind-translate': {
    id: 'chartmind-translate',
    featureName: 'ChartMind - Medical Translation',
    featureDescription: 'Translate medical text between languages with clinical accuracy',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-translate'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[TEXT TO TRANSLATE]**: The medical text content that needs translation
- **[SOURCE LANGUAGE (fromLanguage)]**: The language the text is currently in (referenced as {{fromLanguage}} in the prompt)
- **[TARGET LANGUAGE (toLanguage)]**: The language to translate into (referenced as {{toLanguage}} in the prompt)

Preserve medical terminology appropriateness for the target language. Use culturally appropriate phrasing for patient communication.`,
    model: 'gemini-2.0-flash',
    temperature: 0.2,
    maxTokens: 1000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'ChartMindRecordingIOS',
      file: 'features/chartmind/components/chartmind/services/translationService.js',
    },
  },
  
  'chartmind-required-sections-tracker': {
    id: 'chartmind-required-sections-tracker',
    featureName: 'ChartMind - Required Sections Tracker',
    featureDescription: 'Analyzes transcript to determine which required chart sections have been adequately discussed',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-required-sections-tracker'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[PATIENT ENCOUNTER TRANSCRIPT]**: The clinician-patient conversation to analyze for section coverage
- **[REQUIRED SECTIONS TO CHECK]**: A structured list of section keys and their display names that must be evaluated (e.g., "chief_complaint", "hpi", "ros_constitutional", "physical_exam_general", etc.)

For EACH section in the required list, you must determine whether the transcript adequately addresses it. Return a boolean (detected: true/false) with a confidence score and evidence quote for each section. The section keys in your response must exactly match those provided in the input.`,
    model: 'gemini-2.0-flash',
    temperature: 0.1,
    maxTokens: 1000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'RequiredSectionsTracker',
      file: 'components/chartmind/RequiredSectionsTracker.jsx',
    },
  },

  'chartmind-format-physical-exam': {
    id: 'chartmind-format-physical-exam',
    featureName: 'ChartMind - Format Physical Exam',
    featureDescription: 'Reformats physical exam text so each system is on its own line with "SectionName: findings" for merge rules',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-format-physical-exam'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with physical exam text (possibly unstructured or in paragraph form). Output the same content reformatted so every system is on its own line as "SectionName: findings".`,
    model: 'gemini-2.0-flash',
    temperature: 0.2,
    maxTokens: 2000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'ChartMindChartGeneration',
      file: 'features/chartmind/components/chartmind/ChartMindChartGeneration.jsx',
    },
  },
  'chartmind-v2-ddx': {
    id: 'chartmind-v2-ddx',
    featureName: 'ChartMind V2 - Differential Diagnosis',
    featureDescription: 'V2 additive differential diagnosis analysis from encounter transcript',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-v2-ddx'],
    contextProvided: 'PATIENT ENCOUNTER TRANSCRIPT plus optional DDX-Plus appendix and existing differential.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2Page', file: 'features/chartmind-v2/components/ChartMindV2Page.jsx' },
  },
  'chartmind-v2-ddx-final': {
    id: 'chartmind-v2-ddx-final',
    featureName: 'ChartMind V2 - Final DDX with Test Results',
    featureDescription: 'Updates V2 DDX based on diagnostic results',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-v2-ddx-final'],
    contextProvided: 'Comprehensive encounter context including existing differential and test results.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2Page', file: 'features/chartmind-v2/components/ChartMindV2Page.jsx' },
  },
  'chartmind-v2-clarifying-questions': {
    id: 'chartmind-v2-clarifying-questions',
    featureName: 'ChartMind V2 - Clarifying Questions',
    featureDescription: 'Structured V2 clarification prompts for DDx refinement',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-v2-clarifying-questions'],
    contextProvided: 'Encounter transcript and current differential list.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 1500,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2Page', file: 'features/chartmind-v2/components/ChartMindV2Page.jsx' },
  },
  'chartmind-v2-diagnostic-tests': {
    id: 'chartmind-v2-diagnostic-tests',
    featureName: 'ChartMind V2 - Diagnostic Tests',
    featureDescription: 'V2 diagnostic test recommendations',
    category: 'chartmind',
    // TODO: update Secret Manager prompt llm-p1-chartmind-v2-diagnostic-tests to request pertains_to_conditions per test
    responseFormat: RESPONSE_FORMATS['chartmind-v2-diagnostic-tests'],
    contextProvided: 'Enabled diagnosis set and encounter transcript.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 1500,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2ChartPage', file: 'features/chartmind-v2/components/ChartMindV2ChartPage.jsx' },
  },
  'chartmind-v2-treatment-ed': {
    id: 'chartmind-v2-treatment-ed',
    featureName: 'ChartMind V2 - Treatment (ED)',
    featureDescription: 'Emergency department treatment recommendations',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-v2-treatment-ed'],
    contextProvided: 'Encounter context and confirmed diagnoses for emergency department workflow.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2ChartPage', file: 'features/chartmind-v2/components/ChartMindV2ChartPage.jsx' },
  },
  'chartmind-v2-treatment-pc': {
    id: 'chartmind-v2-treatment-pc',
    featureName: 'ChartMind V2 - Treatment (PC)',
    featureDescription: 'Primary care treatment recommendations',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-v2-treatment-pc'],
    contextProvided: 'Encounter context and confirmed diagnoses for primary care workflow.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2ChartPage', file: 'features/chartmind-v2/components/ChartMindV2ChartPage.jsx' },
  },
  'chartmind-v2-treatment-uc': {
    id: 'chartmind-v2-treatment-uc',
    featureName: 'ChartMind V2 - Treatment (UC)',
    featureDescription: 'Urgent care treatment recommendations',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-v2-treatment-uc'],
    contextProvided: 'Encounter context and confirmed diagnoses for urgent care workflow.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2ChartPage', file: 'features/chartmind-v2/components/ChartMindV2ChartPage.jsx' },
  },
  'chartmind-v2-chart-generation-ed': {
    id: 'chartmind-v2-chart-generation-ed',
    featureName: 'ChartMind V2 - Chart Generation (ED)',
    featureDescription: 'Emergency department chart note generation',
    category: 'chartmind',
    prompt: CHARTMIND_V2_CHART_GENERATION_P1_PROMPT,
    responseFormat: RESPONSE_FORMATS['chartmind-v2-chart-generation-ed'],
    contextProvided: 'Encounter transcript, diagnoses, tests, and treatments for emergency department charting.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 8000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2ChartPage', file: 'features/chartmind-v2/components/ChartMindV2ChartPage.jsx' },
  },
  'chartmind-v2-chart-generation-pc': {
    id: 'chartmind-v2-chart-generation-pc',
    featureName: 'ChartMind V2 - Chart Generation (PC)',
    featureDescription: 'Primary care chart note generation',
    category: 'chartmind',
    prompt: CHARTMIND_V2_CHART_GENERATION_P1_PROMPT,
    responseFormat: RESPONSE_FORMATS['chartmind-v2-chart-generation-pc'],
    contextProvided: 'Encounter transcript, diagnoses, tests, and treatments for primary care charting.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 8000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2ChartPage', file: 'features/chartmind-v2/components/ChartMindV2ChartPage.jsx' },
  },
  'chartmind-v2-chart-generation-uc': {
    id: 'chartmind-v2-chart-generation-uc',
    featureName: 'ChartMind V2 - Chart Generation (UC)',
    featureDescription: 'Urgent care chart note generation',
    category: 'chartmind',
    prompt: CHARTMIND_V2_CHART_GENERATION_P1_PROMPT,
    responseFormat: RESPONSE_FORMATS['chartmind-v2-chart-generation-uc'],
    contextProvided: 'Encounter transcript, diagnoses, tests, and treatments for urgent care charting.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 8000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2ChartPage', file: 'features/chartmind-v2/components/ChartMindV2ChartPage.jsx' },
  },
  'chartmind-v2-discharge-plan': {
    id: 'chartmind-v2-discharge-plan',
    featureName: 'ChartMind V2 - Discharge Plan',
    featureDescription: 'Patient-friendly discharge instructions for v2 encounters',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-v2-discharge-plan'],
    contextProvided: 'Encounter context, diagnoses, and selected treatments.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 2000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2ChartPage', file: 'features/chartmind-v2/components/ChartMindV2ChartPage.jsx' },
  },
  'chartmind-v2-peerview-consultation': {
    id: 'chartmind-v2-peerview-consultation',
    featureName: 'ChartMind V2 - PeerView Consultation',
    featureDescription: 'Physician-to-physician consultation for v2 cases',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-v2-peerview-consultation'],
    contextProvided: 'Case context, transcript, and clinician consult question.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2ChartPage', file: 'features/chartmind-v2/components/ChartMindV2ChartPage.jsx' },
  },
  'chartmind-v2-format-physical-exam': {
    id: 'chartmind-v2-format-physical-exam',
    featureName: 'ChartMind V2 - Format Physical Exam',
    featureDescription: 'Reformat physical exam text into one system per line',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-v2-format-physical-exam'],
    contextProvided: 'Unstructured physical exam text requiring formatting normalization.',
    model: 'gemini-2.0-flash',
    temperature: 0.2,
    maxTokens: 2000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2ChartPage', file: 'features/chartmind-v2/components/ChartMindV2ChartPage.jsx' },
  },
  'chartmind-v2-translate': {
    id: 'chartmind-v2-translate',
    featureName: 'ChartMind V2 - Translate',
    featureDescription: 'Translate v2 charting content between languages',
    category: 'chartmind',
    responseFormat: RESPONSE_FORMATS['chartmind-v2-translate'],
    contextProvided: 'Text to translate with fromLanguage and toLanguage context.',
    model: 'gemini-2.0-flash',
    temperature: 0.2,
    maxTokens: 1000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: { component: 'ChartMindV2ChartPage', file: 'features/chartmind-v2/components/ChartMindV2ChartPage.jsx' },
  },

  // ============================================
  // SECURELINK CONVERSATION
  // ============================================

  'securelink-conversation': {
    id: 'securelink-conversation',
    featureName: 'SecureLink - Patient Intake Conversation',
    featureDescription: 'Conducts a focused patient intake conversation during SecureLink check-in. The conversation goals (WHAT to ask about) are provided per-PET via Conversation Templates. This system prompt controls HOW the conversation is conducted.',
    category: 'securelink',
    responseFormat: RESPONSE_FORMATS['securelink-conversation'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[CONVERSATION_GOALS]**: Plain English description of what topics to cover (defined by clinic admin)
- **[PATIENT_CMR_CONTEXT]**: Patient demographics, medications, allergies, medical history (if available via SecureLink)
- **[CONVERSATION_HISTORY]**: Array of previous exchanges in this conversation
- **[MAX_QUESTIONS]**: Maximum number of questions to ask before completing`,
    model: 'gemini-2.0-flash',
    temperature: 0.4,
    maxTokens: 1500,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'CheckInConversation',
      file: 'pages/checkin/CheckInConversation.jsx',
    },
  },

  'conversation-template-question-suggester': {
    id: 'conversation-template-question-suggester',
    featureName: 'Template Editor — Question Suggester',
    featureDescription:
      'Suggests seed questions for a clinical conversation template based on the clinician-provided goals.',
    category: 'securelink',
    responseFormat: RESPONSE_FORMATS['conversation-template-question-suggester'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[CONVERSATION_GOALS]**: Plain English description of what the conversation should accomplish (written by the clinician in the template editor)`,
    model: 'gemini-2.0-flash',
    temperature: 0.4,
    maxTokens: 500,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'ConversationTemplateEditorPage',
      file: 'features/chartmind/components/conversationTemplates/ConversationTemplateEditorPage.jsx',
    },
  },

  'securelink-ros-extraction': {
    id: 'securelink-ros-extraction',
    featureName: 'SecureLink - ROS Extraction',
    featureDescription: 'Extracts structured Review of Systems from intake conversation transcript and findings for the ROS CMR card.',
    category: 'securelink',
    responseFormat: RESPONSE_FORMATS['securelink-ros-extraction'],
    contextProvided: `# CONTEXT PROVIDED

You will be provided with:
- **[CONVERSATION_RESULTS]**: Intake conversation transcript and/or findings (labels and values) from a patient check-in. Use this to populate each of the 10 ROS systems.`,
    model: DEFAULT_MODEL,
    temperature: 0.2,
    maxTokens: 1500,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'PatientCheckInPage',
      file: 'pages/checkin/PatientCheckInPage.jsx',
    },
  },

  // =========================================================================
  // DSCR (Dan's Special Clinical Reference)
  // =========================================================================

  'dscr-tool-discovery': {
    id: 'dscr-tool-discovery',
    featureName: 'DSCR - Tool Discovery',
    featureDescription: 'Given a medical condition, identifies validated clinical scoring tools with PubMed IDs for the DSCR auto-populate pipeline.',
    category: 'dscr',
    contextProvided: `# CONTEXT PROVIDED\n\nYou will be provided with:\n- **[CONDITION_NAME]**: The medical condition to find scoring tools for.`,
    model: 'gemini-2.0-flash',
    temperature: 0.1,
    maxTokens: 2048,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'DscrAutoPopulate',
      file: 'functions/functions/src/dscr/autoPopulate.js',
    },
  },

  'dscr-factor-extraction': {
    id: 'dscr-factor-extraction',
    featureName: 'DSCR - Factor Extraction',
    featureDescription: 'Extracts clinical factors, weights, thresholds, and patient-friendly questions from a PubMed abstract for DSCR.',
    category: 'dscr',
    contextProvided: `# CONTEXT PROVIDED\n\nYou will be provided with:\n- **[ABSTRACT_TEXT]**: The title and abstract of the PubMed paper to extract factors from.`,
    model: 'gemini-2.0-flash',
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'PubmedImportDialog',
      file: 'features/dscr/PubmedImportDialog.jsx',
    },
  },

  'ddx-optimize-vignette-generation': {
    id: 'ddx-optimize-vignette-generation',
    featureName: 'DDX Optimization - Vignette Generation',
    featureDescription: 'Generates benchmark-ready DDx vignettes for optimization dashboard workflows.',
    category: 'ddx_ingest',
    contextProvided:
      '# CONTEXT PROVIDED\n\nYou will receive profile, category, and desired vignette count. Return JSON with benchmark-ready vignette objects.',
    model: DEFAULT_MODEL,
    temperature: 0.2,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: {
      component: 'DdxOptimizationDashboard',
      file: 'pages/llm/DdxOptimizationDashboard.jsx',
    },
  },

  'ddx-prevalence-estimation': {
    id: 'ddx-prevalence-estimation',
    featureName: 'DDX Optimization - Prevalence Estimation',
    featureDescription: 'LLM-assisted prevalence tier estimation for diagnosis calibration by context profile.',
    category: 'ddx_ingest',
    contextProvided:
      '# CONTEXT PROVIDED\n\nYou will receive diagnosis metadata and context profile. Return prevalence tier and rationale JSON.',
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 2048,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: {
      component: 'DdxOptimizationDashboard',
      file: 'pages/llm/DdxOptimizationDashboard.jsx',
    },
  },

  'ddx-prompt-variant-generation': {
    id: 'ddx-prompt-variant-generation',
    featureName: 'DDX Optimization - Prompt Variant Generation',
    featureDescription: 'Generates constrained prompt variants for Query Manager experimentation.',
    category: 'ddx_ingest',
    contextProvided:
      '# CONTEXT PROVIDED\n\nYou will receive base prompt and optimization objective. Return candidate prompt variants in JSON.',
    model: DEFAULT_MODEL,
    temperature: 0.3,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: { isPartOfChain: false, chainId: null, chainPosition: null },
    uiLocation: {
      component: 'QueryManagementModal',
      file: 'components/llmManager/QueryManagementModal.jsx',
    },
  },

  'ddx-temporal-extraction': {
    id: 'ddx-temporal-extraction',
    featureName: 'DDX-Plus — temporal / feature extraction',
    featureDescription:
      'Structured temporal and clinical feature extraction from transcript for KB projection (Phase 1). Register in Query Manager; used by ChartMind / Symptom Checker when DDX_PLUS_CANT_MISS is enabled.',
    category: 'ddx_ingest',
    prompt: DDX_TEMPORAL_EXTRACTION_SYSTEM_PROMPT,
    contextProvided: `# CONTEXT PROVIDED

You will receive user input containing:
- **CLINICAL_TRANSCRIPT** — encounter or chat text
- **KNOWN_FEATURE_IDS_JSON** — JSON array of KB feature ids to prefer when matching`,
    responseFormat: RESPONSE_FORMATS['ddx-temporal-extraction'],
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'ChartMindDDX',
      file: 'features/chartmind/components/chartmind/hooks/useChartMindDDX.js',
    },
  },

  'ddx-chief-complaint-resolution': {
    id: 'ddx-chief-complaint-resolution',
    featureName: 'DDX-Plus Phase 3 — chief complaint resolution',
    featureDescription:
      'Maps transcript language to one or more KB chief complaint ids with confidence; supports ambiguous and compound presentations.',
    category: 'ddx_ingest',
    prompt: DDX_CHIEF_COMPLAINT_RESOLUTION_SYSTEM_PROMPT,
    contextProvided: `# CONTEXT PROVIDED

You will receive:
- **CLINICAL_TRANSCRIPT** — encounter or chat text
- **PATIENT_CONTEXT_JSON** — structured patient context from the same encounter (may be empty)`,
    responseFormat: RESPONSE_FORMATS['ddx-chief-complaint-resolution'],
    model: DEFAULT_MODEL,
    temperature: 0.1,
    maxTokens: 2048,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'ChartMindDDX',
      file: 'services/ddx/ddxChiefComplaintResolutionService.js',
    },
  },

  'ddx-source-extraction': {
    id: 'ddx-source-extraction',
    featureName: 'DDX-Plus Phase 2 — source extraction',
    featureDescription:
      'Structured graph extraction from allowlisted search snippets. Invoked by ddxAuthoritativeSourceConsult Cloud Function.',
    category: 'ddx_ingest',
    prompt: DDX_SOURCE_EXTRACTION_SYSTEM_PROMPT,
    contextProvided: '',
    responseFormat: RESPONSE_FORMATS['ddx-source-extraction'],
    model: 'gemini-2.0-flash',
    temperature: 0,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'DdxPlusAdminPage',
      file: 'pages/llm/DdxPlusAdminPage.jsx',
    },
  },

  'ddx-discrepancy-detection': {
    id: 'ddx-discrepancy-detection',
    featureName: 'DDX-Plus Phase 2 — discrepancy detection',
    featureDescription: 'Compares KB vs source extraction output; used by audit worker.',
    category: 'ddx_ingest',
    prompt: DDX_DISCREPANCY_DETECTION_SYSTEM_PROMPT,
    contextProvided: '',
    responseFormat: RESPONSE_FORMATS['ddx-discrepancy-detection'],
    model: 'gemini-2.0-flash',
    temperature: 0,
    maxTokens: 2048,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'DdxVerifyPage',
      file: 'pages/llm/DdxVerifyPage.jsx',
    },
  },

  'ddx-ingest-ai-review': {
    id: 'ddx-ingest-ai-review',
    featureName: 'DDX KB ingest — AI row review',
    featureDescription:
      'Batch review of staged chief-complaint rows (JSON findings). Set model to gemini-* for Gemini 2.x or claude-* for Anthropic. Used by Cloud Function ddxIngestAiReviewWorker.',
    category: 'ddx_ingest',
    prompt: DDX_AI_REVIEW_SYSTEM_PROMPT,
    contextProvided: '',
    responseFormat: '',
    model: 'gemini-2.0-flash',
    temperature: 0,
    maxTokens: 8000,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'DdxIngestPage',
      file: 'pages/llm/DdxIngestPage.jsx',
    },
  },

  'ddx-ingest-grounded-references': {
    id: 'ddx-ingest-grounded-references',
    featureName: 'DDX KB ingest — grounded reference corpus',
    featureDescription:
      'Upload/embed reference materials under this query id so vectors get baseQueryId ddx-ingest-grounded-references. Turn on Document Settings (e.g. supplement authoritative resources) so indexing runs.',
    category: 'ddx_ingest',
    prompt:
      'This query id is for reference document uploads and embeddings only — not for conversational LLM calls. Grounded fix generation retrieves chunks where llmDocumentVectors.baseQueryId equals this id.',
    contextProvided: '',
    responseFormat: '',
    model: 'gemini-2.0-flash',
    temperature: 0,
    maxTokens: 256,
    geographicScope: 'global',
    regions: [],
    localities: [],
    documentSettings: {
      supplementAuthoritativeResources: true,
      limitToVectorStore: false,
    },
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'DdxIngestPage',
      file: 'pages/llm/DdxIngestPage.jsx',
    },
  },

  'ddx-ingest-grounded-fix': {
    id: 'ddx-ingest-grounded-fix',
    featureName: 'DDX KB ingest — grounded fix (RAG)',
    featureDescription:
      'Reference-backed suggested fixes for AI findings. Index documents under baseQueryId ddx-ingest-grounded-references; optional SCRAPE_ALLOWLIST_DOMAINS filters URL-sourced chunks.',
    category: 'ddx_ingest',
    prompt: DDX_GROUNDED_FIX_SYSTEM_PROMPT,
    contextProvided: '',
    responseFormat: DDX_GROUNDED_FIX_RESPONSE_FORMAT,
    model: 'gemini-2.0-flash',
    temperature: 0,
    maxTokens: 4096,
    geographicScope: 'global',
    regions: [],
    localities: [],
    chainConfig: {
      isPartOfChain: false,
      chainId: null,
      chainPosition: null,
    },
    uiLocation: {
      component: 'DdxIngestPage',
      file: 'pages/llm/DdxIngestPage.jsx',
    },
  },

};

const DEFAULT_DDX_PLUS_HINT_APPENDIX =
  'When DDX-Plus mode is on, the client may append a KB grounding block to the encounter or user payload. Position 1 remains the global prompt from Query Manager; that block is not auto-generated from P1 text alone.';

const DEFAULT_DDX_PLUS_HINT_DEDICATED =
  'This query runs only when DDX-Plus mode is on and follows the dedicated DDX-Plus contract for this feature.';

/**
 * @param {string|null|undefined} queryId
 * @returns {'none'|'encounter_appendix'|'dedicated_plus_query'}
 */
export function getDdxPlusTier(queryId) {
  if (!queryId) return 'none';
  const meta = QUERY_METADATA[queryId];
  return meta?.ddxPlus?.tier ?? 'none';
}

/**
 * Tooltip / alert copy for Query Manager and Registry (uses operatorNote when set).
 * @param {string|null|undefined} queryId
 * @returns {string}
 */
export function getDdxPlusOperatorHint(queryId) {
  if (!queryId) return '';
  const meta = QUERY_METADATA[queryId];
  const note = meta?.ddxPlus?.operatorNote;
  if (note) return note;
  const tier = meta?.ddxPlus?.tier ?? 'none';
  if (tier === 'encounter_appendix') return DEFAULT_DDX_PLUS_HINT_APPENDIX;
  if (tier === 'dedicated_plus_query') return DEFAULT_DDX_PLUS_HINT_DEDICATED;
  return '';
}

/**
 * @param {string|null|undefined} queryId
 * @returns {boolean}
 */
export function shouldShowDdxPlusRegistryBadge(queryId) {
  return getDdxPlusTier(queryId) !== 'none';
}

/**
 * Get query metadata by ID (no prompt text; P1 prompt from Secret Manager or P1_PROMPTS_DIR).
 * @param {string} queryId - The query ID
 * @returns {Object|null} The query metadata or null
 */
export function getQueryMetadata(queryId) {
  return QUERY_METADATA[queryId] || null;
}

/**
 * Get all query metadata (for scripts; no prompt text).
 * @returns {Object} All metadata keyed by ID
 */
export function getAllQueryMetadata() {
  return { ...QUERY_METADATA };
}


export const getPromptsByCategory = (category) => Object.values(QUERY_METADATA).filter(p => p.category === category);
export const getPromptCategories = () => [...new Set(Object.values(QUERY_METADATA).map(p => p.category))];
